import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, InputAdornment,
  IconButton, CircularProgress, Avatar, Divider,
} from '@mui/material';
import {
  Visibility, VisibilityOff, Church, Person, Lock,
  ArrowForward, ShieldOutlined, TrendingUp, AccountBalanceWallet,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authLoginURL } from '../BaseURL';
import { useNotify } from '../notify';

const HIGHLIGHTS = [
  { icon: <TrendingUp sx={{ fontSize: 18 }} />,          title: 'Weekly cash tracking',        desc: 'Every receipt, every payment.' },
  { icon: <AccountBalanceWallet sx={{ fontSize: 18 }} />, title: 'Cash book & bank balances',   desc: 'Monthly balances at a glance.' },
  { icon: <ShieldOutlined sx={{ fontSize: 18 }} />,      title: 'Role-based access',           desc: 'Fine-grained per-module control.' },
];

export default function Login({ onLogin, churchInfo }) {
  const navigate = useNavigate();
  const notify   = useNotify();
  const [form, setForm]       = useState({ username: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.username || !form.password) {
      notify.warning('Please enter your username and password.', { title: 'Missing details' });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(authLoginURL, form);
      if (res.data.success) {
        onLogin(res.data.user, res.data.token);
        notify.success(`Welcome, ${res.data.user.full_name}.`, { title: 'Signed in' });
        navigate('/');
      }
    } catch (err) {
      notify.error(
        err.response?.data?.message || 'Check your credentials and try again.',
        { title: 'Login failed' }
      );
    } finally {
      setLoading(false);
    }
  };

  const name = churchInfo?.name || 'The Church';
  const logo = churchInfo?.logo;
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: '#F5F7FA' }}>
      {/* ═══════════════ LEFT — Branded panel ═══════════════ */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          minWidth: 420,
          maxWidth: 560,
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          background: 'linear-gradient(160deg, #0B2545 0%, #13315C 55%, #061529 100%)',
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blooms */}
        <Box sx={{
          position: 'absolute', top: -100, right: -80,
          width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(200,146,17,0.28) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -140, left: -100,
          width: 380, height: 380, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: '40%', right: 40,
          width: 3, height: 100, background: 'linear-gradient(180deg, transparent, #C89211, transparent)',
          opacity: 0.4,
        }} />

        {/* Top — brand mark */}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: '#C89211',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 18px rgba(200,146,17,0.35)',
          }}>
            <Church sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>
            Accounting Portal
          </Typography>
        </Box>

        {/* Middle — hero */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {logo && (
            <Avatar
              src={logo}
              sx={{
                width: 88, height: 88, mb: 3,
                border: '3px solid rgba(255,255,255,0.15)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
              }}
            />
          )}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: 2,
              fontSize: { md: 40, lg: 44 },
            }}
          >
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box sx={{ width: 36, height: 2, bgcolor: '#C89211' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic' }}>
              Faithful stewardship, made simple.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {HIGHLIGHTS.map((h, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: 1.5,
                  bgcolor: 'rgba(200,146,17,0.12)',
                  border: '1px solid rgba(200,146,17,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#E0A82E', flexShrink: 0,
                }}>
                  {h.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#fff', lineHeight: 1.3 }}>
                    {h.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {h.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom — meta */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981',
              boxShadow: '0 0 0 3px rgba(16,185,129,0.2)',
            }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: '0.04em' }}>
              System online
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block' }}>
            {today}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'block', mt: 0.5 }}>
            © {new Date().getFullYear()} · Church Accounting System · v1.0
          </Typography>
        </Box>
      </Box>

      {/* ═══════════════ RIGHT — Sign-in form ═══════════════ */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        p: { xs: 2.5, sm: 4, md: 6 },
        position: 'relative',
      }}>
        {/* Mobile brand strip */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'absolute', top: 0, left: 0, right: 0,
          alignItems: 'center', gap: 1.5, p: { xs: 2, sm: 2.5 },
          background: 'linear-gradient(160deg, #0B2545 0%, #13315C 100%)',
          color: '#fff', zIndex: 1,
        }}>
          {logo ? (
            <Avatar src={logo} sx={{ width: 36, height: 36 }} />
          ) : (
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5, bgcolor: '#C89211',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Church sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
          )}
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>{name}</Typography>
            <Typography variant="caption" sx={{ color: '#E0A82E', letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 9, fontWeight: 700 }}>
              Accounting Portal
            </Typography>
          </Box>
        </Box>

        <Box sx={{ width: '100%', maxWidth: 400, mt: { xs: 10, sm: 12, md: 0 } }}>
          <Typography
            variant="caption"
            sx={{
              color: 'secondary.main', fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
            }}
          >
            Sign In
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, mb: 0.5, letterSpacing: '-0.02em' }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Enter your credentials to access the accounting workspace.
          </Typography>

          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.primary', display: 'block', mb: 0.75, letterSpacing: '0.02em' }}>
              Username
            </Typography>
            <TextField
              fullWidth name="username" value={form.username} onChange={handleChange}
              placeholder="Enter your username"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: 'text.primary', display: 'block', mb: 0.75, letterSpacing: '0.02em' }}>
              Password
            </Typography>
            <TextField
              fullWidth name="password" value={form.password} onChange={handleChange}
              type={showPwd ? 'text' : 'password'}
              placeholder="Enter your password"
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd(!showPwd)} edge="end" size="small">
                      {showPwd ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            fullWidth variant="contained"
            onClick={handleSubmit} disabled={loading}
            endIcon={loading ? null : <ArrowForward />}
            sx={{
              py: 1.6, fontSize: 15, fontWeight: 700, letterSpacing: '0.02em',
              background: 'linear-gradient(90deg, #0B2545, #13315C)',
              boxShadow: '0 8px 24px rgba(11,37,69,0.22)',
              '&:hover': {
                background: 'linear-gradient(90deg, #061529, #0B2545)',
                boxShadow: '0 10px 30px rgba(11,37,69,0.35)',
              },
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In Securely'}
          </Button>

          <Divider sx={{ my: 4 }}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
              Session secured
            </Typography>
          </Divider>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <ShieldOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Encrypted connection · 8-hour session
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
